import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import POS from "@/pages/POS";
import Reconcile from "@/pages/Reconcile";
import StockTransfers from "@/pages/StockTransfers";
import Movements from "@/pages/Movements";
import Pricing from "@/pages/Pricing";
import Sales from "@/pages/Sales";
import Users from "@/pages/Users";
import Deals from "@/pages/Deals";
import DiscountRequests from "@/pages/DiscountRequests";
import Customers from "@/pages/Customers";
import CustomerProfile from "@/pages/CustomerProfile";
import Loyalty from "@/pages/Loyalty";
import { Toaster } from "@/components/ui/sonner";

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback fragment BEFORE any protected route runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route element={<Layout />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/pos" element={<POS />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/reconcile" element={<RoleGuard roles={["platform_owner","vendor_owner","admin","manager"]}><Reconcile /></RoleGuard>} />
        <Route path="/transfers" element={<RoleGuard roles={["platform_owner","vendor_owner","admin","manager"]}><StockTransfers /></RoleGuard>} />
        <Route path="/movements" element={<Movements />} />
        <Route path="/pricing" element={<RoleGuard roles={["platform_owner","vendor_owner","admin","manager"]}><Pricing /></RoleGuard>} />
        <Route path="/deals" element={<RoleGuard roles={["platform_owner","vendor_owner","admin","manager"]}><Deals /></RoleGuard>} />
        <Route path="/discount-requests" element={<RoleGuard roles={["platform_owner","vendor_owner","admin","manager"]}><DiscountRequests /></RoleGuard>} />
        <Route path="/sales" element={<Sales />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/customers/:id" element={<CustomerProfile />} />
        <Route path="/loyalty" element={<RoleGuard roles={["platform_owner","vendor_owner","admin"]}><Loyalty /></RoleGuard>} />
        <Route path="/users" element={<RoleGuard roles={["platform_owner","vendor_owner","admin"]}><Users /></RoleGuard>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function RoleGuard({ roles, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (!roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
